/**
 * The `ps_known_user` cookie tells the marketing site that this browser has signed in to the app
 * before, so it can split customers (who click Login) from prospects in its analytics (ENG-2495).
 *
 * It is set on `.paintscout.com` so every `*.paintscout.com` site can read it. It is not `HttpOnly`,
 * because the marketing site reads it from JavaScript. The value holds coarse buckets only — never a
 * user id, company id or email.
 *
 * Sign-out does not clear it: the question it answers is "has this browser signed in before".
 */

export const KNOWN_USER_COOKIE = 'ps_known_user'

/** Only prod writes the cookie. Dev runs on `app.paintscout.dev`; test and staging use other hosts. */
export const KNOWN_USER_HOST = 'app.paintscout.com'

export const KNOWN_USER_COOKIE_DOMAIN = '.paintscout.com'

/** One year. Every signed-in load rewrites the cookie, so an active user never reaches it. */
export const KNOWN_USER_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

/** Bump when the value format changes, so the marketing site can tell old values apart. */
export const KNOWN_USER_COOKIE_VERSION = '1'

export type KnownUserStatus = 'trial' | 'active' | 'churned' | 'inactive'

/** Customer roles, as `getHighestRole` returns them. PaintScout staff roles are not in this list. */
const CUSTOMER_ROLES = ['corporate', 'core', 'team', 'painter', 'admin', 'sales', 'crew', 'viewer']

export interface KnownUserInput {
  hostname: string
  highestRole?: string
  isTrial?: boolean
  isSubscribed?: boolean
  isChurned?: boolean
  isMasquerading?: boolean
  isE2E: boolean
}

export function getKnownUserStatus({
  isTrial,
  isSubscribed,
  isChurned
}: Pick<KnownUserInput, 'isTrial' | 'isSubscribed' | 'isChurned'>): KnownUserStatus {
  if (isTrial) {
    return 'trial'
  }
  if (isSubscribed) {
    return 'active'
  }
  if (isChurned) {
    return 'churned'
  }
  return 'inactive'
}

/**
 * The cookie value, or `null` when this session must not write one: a non-prod host, a masquerade,
 * an E2E run, or a PaintScout staff user (superadmin, support, paintscout_sales). Staff are not
 * customers, and counting them as customers would skew the same analytics this cookie exists for.
 */
export function getKnownUserCookieValue(input: KnownUserInput): string | null {
  const { hostname, highestRole, isMasquerading, isE2E } = input
  if (hostname !== KNOWN_USER_HOST || isMasquerading || isE2E) {
    return null
  }
  if (!highestRole || !CUSTOMER_ROLES.includes(highestRole)) {
    return null
  }

  const params = new URLSearchParams({
    v: KNOWN_USER_COOKIE_VERSION,
    status: getKnownUserStatus(input),
    role: highestRole
  })
  // `&` and `=` are not safe in a cookie value. Readers reverse this with
  // `new URLSearchParams(decodeURIComponent(value))`.
  return encodeURIComponent(params.toString())
}

export function writeKnownUserCookie(value: string) {
  document.cookie =
    `${KNOWN_USER_COOKIE}=${value}; Domain=${KNOWN_USER_COOKIE_DOMAIN}; Path=/; ` +
    `Max-Age=${KNOWN_USER_MAX_AGE_SECONDS}; Secure; SameSite=Lax`
}
